import React, { useState } from 'react';
import {
	ArrowRight,
	CheckCircle,
	ChevronDown,
	Clock,
	RefreshCw,
	X,
	Upload,
	Download,
	Square,
	CheckSquare,
} from 'lucide-react';

interface SyncActivityCardProps {
	activity: any;
	onRetry: (syncLogId: string, event: React.MouseEvent) => void;
	isRetrying: boolean;
	onMarkAsManuallyTriggered: (syncLogId: string, event: React.MouseEvent) => void;
	isMarkingManually: boolean;
	onClick: () => void;
	isSelected?: boolean;
	onSelect?: (syncLogId: string, isSelected: boolean) => void;
}

export const SyncActivityCard: React.FC<SyncActivityCardProps> = ({
	activity,
	onRetry,
	isRetrying,
	onMarkAsManuallyTriggered,
	isMarkingManually,
	onClick,
	isSelected = false,
	onSelect,
}) => {
	const [showError, setShowError] = useState(false);
	const isCompleted = activity.status === 'SUCCESS' || activity.status === 'COMPLETED';
	const isFailed = activity.status === 'FAILED';
	const isManuallyTriggered = activity.status === 'MANUALLY_TRIGGERED';

	const formatTimestamp = (timestamp: string) => {
		if (!timestamp) return 'No timestamp';
		const date = new Date(timestamp);
		if (isNaN(date.getTime())) return 'Invalid timestamp';
		return date.toLocaleString('en-US', {
			month: '2-digit',
			day: '2-digit',
			year: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
			second: '2-digit',
		});
	};

	return (
		<div
			className={`border rounded-lg bg-white transition-all ${
				isSelected ? 'border-blue-500 shadow-md ring-2 ring-blue-200' : 'hover:shadow-sm'
			}`}>
			{/* Header Section */}
			<div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
				<div className="flex items-center space-x-3 flex-1 min-w-0">
					{/* Checkbox for failed syncs */}
					{isFailed && onSelect && (
						<button
							onClick={(e) => {
								e.stopPropagation();
								onSelect(activity.id, !isSelected);
							}}
							className={`flex-shrink-0 p-1 rounded transition-all ${
								isSelected
									? 'bg-blue-100 text-blue-600 hover:bg-blue-200'
									: 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'
							}`}
							title={isSelected ? 'Deselect for bulk retry' : 'Select for bulk retry'}>
							{isSelected ? <CheckSquare className="h-5 w-5" /> : <Square className="h-5 w-5" />}
						</button>
					)}
					<h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 flex-1 min-w-0">
						<span>
							{activity.clientName} -{' '}
							{activity.entityType
								?.toLowerCase()
								.split('_')
								.map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
								.join(' ')}{' '}
							{activity.action?.toLowerCase().charAt(0).toUpperCase() + activity.action?.toLowerCase().slice(1)}
						</span>
						{activity.entityId && (
							<span className="inline-flex items-center px-2 py-0.5 rounded border border-gray-300 bg-gray-50 text-gray-600 font-mono text-xs font-normal">
								{activity.entityId}
							</span>
						)}
					</h3>
				</div>
				<div className="flex items-center space-x-2 ml-4">
					{isFailed && !isRetrying && !isMarkingManually && (
						<>
							<button
								onClick={(e) => {
									e.stopPropagation();
									onRetry(activity.id, e);
								}}
								className="inline-flex items-center px-3 py-1 text-xs font-medium text-blue-700 bg-white border border-blue-300 rounded-md hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors">
								Retry
							</button>
							<button
								onClick={(e) => {
									e.stopPropagation();
									onMarkAsManuallyTriggered(activity.id, e);
								}}
								className="inline-flex items-center px-3 py-1 text-xs font-medium text-orange-700 bg-white border border-orange-300 rounded-md hover:bg-orange-50 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-colors">
								Mark Manually Triggered
							</button>
						</>
					)}
					{isRetrying && (
						<button
							disabled
							className="inline-flex items-center px-3 py-1 text-xs font-medium text-gray-400 bg-gray-100 border border-gray-300 rounded-md cursor-not-allowed">
							<RefreshCw className="animate-spin h-3.5 w-3.5 mr-1.5" />
							Retrying...
						</button>
					)}
					{isMarkingManually && (
						<button
							disabled
							className="inline-flex items-center px-3 py-1 text-xs font-medium text-gray-400 bg-gray-100 border border-gray-300 rounded-md cursor-not-allowed">
							<RefreshCw className="animate-spin h-3.5 w-3.5 mr-1.5" />
							Marking...
						</button>
					)}
					<button
						onClick={onClick}
						className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full cursor-pointer hover:opacity-80 transition-opacity ${
							isCompleted
								? 'bg-green-100 text-green-800 hover:bg-green-200'
								: isFailed
									? 'bg-red-100 text-red-800 hover:bg-red-200'
									: isManuallyTriggered
										? 'bg-orange-100 text-orange-800 hover:bg-orange-200'
										: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
						}`}>
						{activity.status}
					</button>
				</div>
			</div>

			{/* Source → Target Flow */}
			<div className="flex items-center space-x-2 px-4 py-2 bg-gray-50 text-xs">
				<span className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-blue-100 text-blue-700 rounded font-medium">
					<Upload className="h-3.5 w-3.5" />
					{activity.sourceSystem}
				</span>
				<div className="flex items-center space-x-0.5 relative">
					<style>{`
						@keyframes slideArrow {
							0%, 100% { opacity: 0.3; transform: translateX(-4px); }
							50% { opacity: 1; transform: translateX(4px); }
						}
						.arrow-animated:nth-child(1) { animation: slideArrow 1.5s ease-in-out infinite; }
						.arrow-animated:nth-child(2) { animation: slideArrow 1.5s ease-in-out 0.2s infinite; }
						.arrow-animated:nth-child(3) { animation: slideArrow 1.5s ease-in-out 0.4s infinite; }
					`}</style>
					<ArrowRight className="h-3.5 w-3.5 text-blue-500 arrow-animated" />
					<ArrowRight className="h-3.5 w-3.5 text-blue-500 arrow-animated -ml-2" />
					<ArrowRight className="h-3.5 w-3.5 text-blue-500 arrow-animated -ml-2" />
				</div>
				<span className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-green-100 text-green-700 rounded font-medium">
					<Download className="h-3.5 w-3.5" />
					{activity.targetSystem}
				</span>
			</div>

			{/* Status Message */}
			{isCompleted && (
				<div className="flex items-start space-x-2 bg-green-50 border-t border-green-200 px-4 py-2">
					<CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
					<p className="text-xs text-green-800 font-medium">Sync completed successfully</p>
				</div>
			)}

			{isManuallyTriggered && (
				<div className="flex items-start space-x-2 bg-orange-50 border-t border-orange-200 px-4 py-2">
					<CheckCircle className="h-4 w-4 text-orange-600 flex-shrink-0 mt-0.5" />
					<p className="text-xs text-orange-800 font-medium">Manually triggered successfully</p>
				</div>
			)}

			{isFailed && activity.error && (
				<div className="bg-red-50 border-t border-red-200">
					<button
						onClick={(e) => {
							e.stopPropagation();
							setShowError(!showError);
						}}
						className="flex items-center justify-between w-full px-4 py-2 text-left hover:bg-red-100 transition-colors">
						<div className="flex items-center space-x-2">
							<X className="h-4 w-4 text-red-600 flex-shrink-0" />
							<p className="text-xs font-semibold text-red-900">Failed</p>
						</div>
						<ChevronDown className={`h-4 w-4 text-red-600 transition-transform ${showError ? 'rotate-180' : ''}`} />
					</button>
					{showError && (
						<div className="px-4 pb-3 pl-10">
							<p className="text-xs text-red-800 font-mono break-words whitespace-pre-wrap">{activity.error}</p>
						</div>
					)}
				</div>
			)}

			{/* Metadata Footer */}
			<div className="flex items-center space-x-3 px-4 py-2 bg-gray-50 border-t border-gray-100 text-xs text-gray-500">
				<span className="flex items-center space-x-1">
					<Clock className="h-3 w-3" />
					<span>{formatTimestamp(activity.createdAt || activity.timestamp || activity.updatedAt)}</span>
				</span>
				{activity.processingTime && <span>{activity.processingTime}ms</span>}
				<span>{activity.attemptCount || 0} attempts</span>
			</div>
		</div>
	);
};
